import type { ReactElement } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRouteError } from "react-router-dom";

import { AuthPageLoading } from "@/components/layout/AuthPageShell";
import { Button } from "@/components/ui/button";

/**
 * App-wide error boundary. Its main job is to make transient DOM crashes
 * invisible to the user.
 *
 * Browser features and extensions outside our control — page auto-translation,
 * iOS/password-manager autofill, etc. — mutate the DOM while React owns it. When
 * that happens mid-commit (most often during the shell remount when signing in
 * as a different user), React throws `NotFoundError` from `insertBefore` /
 * `removeChild` and the whole tree unmounts. A full reload always fixes it
 * (clean DOM), which is why a manual hard refresh "works".
 *
 * A second, unrelated but equally recoverable class is the "stale chunk" error:
 * after a new deploy, Vite emits route chunks with fresh content hashes and the
 * old files (e.g. `Projects-BIm2oMzA.js`) 404. A tab opened before the deploy
 * still references the old hashes, so the next lazy `import()` throws "Failed to
 * fetch dynamically imported module". Reloading pulls a fresh `index.html` with
 * the new hashes and fixes it.
 *
 * So for those recoverable error classes we reload once automatically
 * (rate-limited to avoid reload loops). Anything else shows a localized fallback.
 */
const RELOAD_TIMESTAMP_KEY = "konstrulog:lastErrorReloadAt";
const RELOAD_COOLDOWN_MS = 10_000;

function isTransientDomError(error: unknown): boolean {
  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    return error.name === "NotFoundError" || error.name === "HierarchyRequestError";
  }
  if (error instanceof Error) {
    return /insertBefore|removeChild|not a child|object can ?not be found/i.test(error.message);
  }
  return false;
}

function isStaleChunkError(error: unknown): boolean {
  if (error instanceof Error) {
    return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Outdated Optimize Dep/i.test(
      error.message,
    );
  }
  return false;
}

function isAutoRecoverableError(error: unknown): boolean {
  return isTransientDomError(error) || isStaleChunkError(error);
}

function lastReloadAt(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? 0);
  } catch {
    return 0;
  }
}

export function AppRouteErrorBoundary(): ReactElement {
  const error = useRouteError();
  const { t } = useTranslation();

  // Decide once, on mount, whether this render should trigger an auto-reload.
  const [isAutoRecovering] = useState(
    () => isAutoRecoverableError(error) && Date.now() - lastReloadAt() > RELOAD_COOLDOWN_MS,
  );

  useEffect(() => {
    if (!isAutoRecovering) return;
    try {
      window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
    } catch {
      /* sessionStorage may be unavailable; reload anyway */
    }
    window.location.reload();
  }, [isAutoRecovering]);

  if (isAutoRecovering) {
    // Reload is in flight — show the neutral loader instead of a crash flash.
    return <AuthPageLoading />;
  }

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center text-foreground">
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">{t("errors.boundaryTitle")}</h1>
        <p className="max-w-sm text-sm text-muted-foreground">{t("errors.boundaryBody")}</p>
      </div>
      <Button onClick={() => window.location.reload()}>{t("errors.boundaryReload")}</Button>
    </div>
  );
}
